import {validPriceData} from './bb-sale-prices.js';
import { renderCard } from './bb-card-render-v14.js';
import { StandardEvents } from '@shopify/events';
const instances = new WeakMap();
export function scopeKey(value) {
  const url = new URL(value, location.origin);
  const match = url.pathname.match(/^(.*\/collections\/[^/]+)(?:\/([^/]+))?\/?$/);
  if (url.origin !== location.origin || !match) throw new Error('Invalid related scope.');
  const tags = match[2] ? decodeURIComponent(match[2]).toLowerCase().split('+') : [];
  if (tags.some(tag => !tag || /[\/\u0000-\u001f\u007f]/.test(tag))) throw new Error('Invalid related tags.');
  return url.origin + match[1] + '/' + [...new Set(tags)].sort().join('+');
}
export function filterTag(kind, value) {
  if (!value || /[\u0000-\u001f\u007f]/.test(value)) return '';
  const hex = [...new TextEncoder().encode(value)].map(byte => byte.toString(16).padStart(2, '0')).join('');
  return hex.length <= 250 ? 'bb-' + kind + '-' + hex : '';
}
export function relatedURLs(data, game = '') {
  const base = new URL(data.allUrl || '/collections/all', location.origin);
  if (base.origin !== location.origin || !/\/collections\/[^/]+\/?$/.test(base.pathname)) return [];
  return [['s', data.set], ['a', data.artist]].flatMap(([kind, value]) => {
    // CardUploader's existing set tag includes cards that Flow has not reached yet.
    const tag = kind === 's' && data.setTag ? data.setTag : filterTag(kind, value);
    if (!tag) return [];
    const url = new URL(base);
    url.pathname = url.pathname.replace(/\/$/, '') + '/' + [game, tag].filter(Boolean).sort().join('+');
    url.search = ''; url.searchParams.set('section_id', 'bb-related-feed');
    url.searchParams.set('sort_by', 'created-descending');
    url.searchParams.set('filter.v.availability', '1');
    return [url];
  });
}
export function selectRelated(rows, data, game = '', random = Math.random) {
  const seen = new Set();
  const excluded = new Set(data.excludeIds || []);
  const candidates = rows.filter(row => {
    if (seen.has(row.id) || excluded.has(row.id) || row.id === data.productId || !row.available || game && !row.tags.includes(game)) return false;
    if (!(data.set && row.set === data.set) && !(data.artist && row.artist === data.artist)) return false;
    seen.add(row.id); return true;
  });
  const shuffle = values => {
    for (let i = values.length - 1; i > 0; i--) { const j = Math.floor(random() * (i + 1)); [values[i], values[j]] = [values[j], values[i]]; }
    return values;
  };
  const sets = shuffle(candidates.filter(row => data.set && row.set === data.set));
  const artists = shuffle(candidates.filter(row => data.artist && row.artist === data.artist));
  const result = [], chosen = new Set();
  while ((sets.length || artists.length) && result.length < (Number(data.limit) || 4)) {
    for (const pool of [sets, artists]) {
      let row;
      while ((row = pool.shift()) && chosen.has(row.id)) { /* Skip overlap between pools. */ }
      if (row && result.length < (Number(data.limit) || 4)) { result.push(row); chosen.add(row.id); }
    }
  }
  return result;
}
function readContexts(root) {
  const contexts = [...root.querySelectorAll('[data-bb-related-context]')].map(node => ({ ...node.dataset }));
  return contexts.length ? contexts : root.dataset.productId ? [{ ...root.dataset }] : [];
}
function gameFor(data) {
  const tags = JSON.parse(data.tags || '[]');
  const configured = [...document.querySelectorAll('[data-bb-game-routes] a')].flatMap(link => {
    const match = new URL(link.href, location.origin).pathname.match(/\/collections\/[^/]+\/([^/]+)$/);
    return match ? match[1].split('+') : [];
  });
  return [...configured, 'pokemon-tcg', 'palworld-tcg', 'yu-gi-oh-tcg', 'disney-lorcana-tcg'].find(tag => tags.includes(tag)) || '';
}
export function combineRelated(groups, limit = 4) {
  const result = [], seen = new Set();
  for (let index = 0; groups.some(group => index < group.length) && result.length < limit; index++) {
    for (const group of groups) {
      const row = group[index];
      if (row && !seen.has(row.id) && result.length < limit) { result.push(row); seen.add(row.id); }
    }
  }
  return result;
}
function candidates(root) {
  return [...root.querySelectorAll('[data-bb-related-candidate]')].flatMap(node => {
    try {
      const tags = JSON.parse(node.dataset.tags || '[]'), card = node.querySelector('[data-bb-card]');
      if (!card || card.dataset.productId !== node.dataset.id || !Array.isArray(tags)) return [];
      return [{ id: node.dataset.id, set: node.dataset.set, artist: node.dataset.artist, tags, available: node.dataset.available === 'true', card }];
    } catch { return []; }
  });
}
export function unpackRelated(value) {
  if (!Array.isArray(value) || value.length > 48) throw new Error('Invalid related cards.');
  return value.map(row => {
    if (!Array.isArray(row) || row.length !== 7 || !/^\d+$/.test(row[0]) || !row.slice(1, 5).every(item => typeof item === 'string') || !Array.isArray(row[5]) || !row[5].every(tag => typeof tag === 'string') || !Array.isArray(row[6]) || (![10,11,12].includes(row[6].length) || row[6].length >= 11 && typeof row[6][10] !== 'string' || row[6].length === 12 && !validPriceData(row[6][11]))) throw new Error('Invalid related card.');
    return { id: row[0], set: row[1], artist: row[2], title: row[3], url: row[4], tags: row[5], card: row[6], available: true };
  });
}
export class RelatedProducts {
  constructor(element) {
    this.element = element;
    this.contexts = readContexts(element);
    this.events = new AbortController();
    element.querySelector('[data-bb-related-retry]')?.addEventListener('click', () => this.load(), { signal: this.events.signal });
    if (element.dataset.cartRelated === 'true') {
      document.addEventListener(StandardEvents.cartLinesUpdate, event => {
        event.promise?.then(() => {
          if (this.events.signal.aborted) return;
          clearTimeout(this.cartTimer);
          this.cartTimer = setTimeout(() => this.refreshCart(), 200);
        }).catch(() => {});
      }, { signal: this.events.signal });
    }
    this.observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      this.observer.disconnect(); this.load();
    }, { rootMargin: '400px' });
    this.observer.observe(element);
  }
  async refreshCart() {
    this.contextRequest?.abort(); this.request?.abort();
    const request = new AbortController(); this.contextRequest = request;
    try {
      const url = new URL(this.element.dataset.cartUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid cart source.');
      url.searchParams.set('section_id', this.element.dataset.sectionId);
      const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal: request.signal });
      if (!response.ok) throw new Error('Cart suggestions unavailable.');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = parsed.querySelector('[data-bb-related-products][data-cart-related="true"]');
      if (!next || next.dataset.sectionId !== this.element.dataset.sectionId) throw new Error('Cart suggestions changed.');
      this.contexts = readContexts(next);
      this.element.dataset.excludes = next.dataset.excludes;
      this.element.dataset.recentFallback = next.dataset.recentFallback;
      await this.load();
    } catch {
      if (!request.signal.aborted) this.showStatus('Suggestions couldn’t refresh. Try again.', true);
    }
  }
  showStatus(message, retry = false) {
    const status = this.element.querySelector('[data-bb-related-status]');
    status.textContent = message; status.hidden = false;
    const button = this.element.querySelector('[data-bb-related-retry]');
    if (button) button.hidden = !retry;
    this.element.setAttribute('aria-busy', 'false');
  }
  async load() {
    this.request?.abort(); this.request = new AbortController();
    const signal = this.request.signal, data = this.element.dataset;
    this.element.setAttribute('aria-busy', 'true');
    const retry = this.element.querySelector('[data-bb-related-retry]'); if (retry) retry.hidden = true;
    try {
      const excludeIds = JSON.parse(data.excludes || '[]').map(String);
      const contexts = this.contexts.slice(0, 3).map(context => ({ ...data, ...context, excludeIds }));
      const urls = contexts.flatMap(context => relatedURLs(context, gameFor(context)));
      if (data.recentFallback === 'true' && !contexts.length) {
        const url = new URL(data.allUrl || '/collections/all', location.origin);
        scopeKey(url);
        url.search = ''; url.searchParams.set('section_id', 'bb-related-feed');
        url.searchParams.set('sort_by', 'created-descending'); urls.push(url);
      }
      const uniqueURLs = [...new Map(urls.map(url => [url.href, url])).values()];
      const feeds = await Promise.allSettled(uniqueURLs.map(async url => {
        const response = await fetch(url.href, { credentials: 'same-origin', signal });
        if (!response.ok) throw new Error('Related cards unavailable.');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = parsed.querySelector('[data-bb-related-feed]');
        if (!feed || scopeKey(feed.dataset.source) !== scopeKey(url)) throw new Error('Related scope changed.');
        return unpackRelated(JSON.parse(feed.querySelector('[data-bb-related-rows]')?.textContent || 'null'));
      }));
      let rows = feeds.flatMap(result => result.status === 'fulfilled' ? result.value : []);
      const choose = () => combineRelated(contexts.map(context => selectRelated(rows, context, gameFor(context))), Number(data.limit) || 4);
      let selected = choose();
      if (!contexts.length && data.recentFallback === 'true') {
        const seen = new Set(excludeIds);
        selected = rows.filter(row => { if (seen.has(row.id)) return false; seen.add(row.id); return true; });
        for (let i = selected.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [selected[i], selected[j]] = [selected[j], selected[i]]; }
        selected = selected.slice(0, Number(data.limit) || 4);
      }
      // During the tag backfill, Shopify's own recommendations can supply exact matches.
      if (selected.length < Number(data.limit) && data.nativeUrl) {
        try {
          const url = new URL(data.nativeUrl, location.origin);
          if (url.origin !== location.origin) throw new Error('Invalid source.');
          const response = await fetch(url.href, { credentials: 'same-origin', signal });
          if (response.ok) {
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            const template = parsed.querySelector('[data-bb-related-native]');
            rows = rows.concat(template ? candidates(template.content) : []);
            selected = choose();
          }
        } catch { signal.throwIfAborted(); }
      }
      signal.throwIfAborted();
      const grid = this.element.querySelector('[data-bb-related-grid]');
      if (!selected.length) {
        const failed = feeds.some(result => result.status === 'rejected');
        this.showStatus(failed ? 'Suggestions couldn’t load. Try again.' : 'No matching suggestions to show just now.', failed);
        if (!failed) grid.replaceChildren();
        if (!grid.children.length) grid.hidden = true;
        return;
      }
      const cards = selected.flatMap(row => { try { return [Array.isArray(row.card) ? renderCard(row) : row.card]; } catch { return []; } });
      if (!cards.length) throw new Error('Cards could not render.');
      grid.replaceChildren(...cards);
      this.element.querySelector('[data-bb-related-status]').hidden = true;
      this.element.hidden = false;
      grid.hidden = false;
      this.element.setAttribute('aria-busy', 'false');
      document.dispatchEvent(new CustomEvent('bb:cards-loaded'));
    } catch {
      if (!signal.aborted) this.showStatus('Suggestions couldn’t load. Try again.', true);
    }
  }
  destroy() { this.observer.disconnect(); this.request?.abort(); this.contextRequest?.abort(); this.events.abort(); clearTimeout(this.cartTimer); }
}
function initialise(root = document) {
  root.querySelectorAll('[data-bb-related-products]').forEach(element => {
    if (!instances.has(element)) instances.set(element, new RelatedProducts(element));
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
else initialise();
document.addEventListener('shopify:section:load', event => initialise(event.target));
document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-bb-related-products]').forEach(element => { instances.get(element)?.destroy(); instances.delete(element); }));
